import { darken, transparentize } from 'polished'

import { themeHelpers } from '@subaio/utils'
import { defaultButtonTheme, defaultInputTheme, defaultSpacing } from '@subaio/webapp'

const animation = {
	default: 'cubic-bezier(0.4, 0, 0.2, 1)',
}

const theme = () => {
	const colors = {
		actionOnLight: '#0000A0',
		actionOnDark: '#0000A0',
		btnBackOnDark: '#FFF',
		btnTextOnDark: '#FFF',
		positive: '#0D8268',
		negative: '#000',
		background: '#F1F2F4',
		navBorders: '#C9C7C7',
		hero: '#272E3D',
		alert: '#E70404',
		warning: '#FFCF3D',
		headlines: '#000',
		text: '#000',
		amountPositive: '#0D8268',
		amountNegative: '#000',
		labelOnLight: '#000',
		labelOnDark: '#FFF',
		disabledText: '#F1F2F4',
		oakPositive: '#0D8268',
		oakNegative: '#E70404',
		chart: '#000',
		heroChart: '#000',
		chartBackground: '#FFF',

		black: '#000',
		white: '#FFF',
	}

	const font = {
		family: {
			header: `'Nordea Sans', sans-serif`,
			base: `'Nordea Sans', sans-serif`,
			code: `'Roboto Mono', monospace`,
		},
		size: {
			largest: '28px',
			larger: '22px',
			large: '18px',
			base: '16px',
			small: '14px',
			smaller: '12px',

			h1: '36px',
			h2: '16px',
			h3: '14px',
		},
	}

	return {
		color: {
			white: colors.white,
			black: colors.black,
			cardBackground: colors.white,
			cardShadow: transparentize(0.88, colors.black),
			cardSubsectionBackground: '#f5f5f5',

			actionLabel: colors.white,
			action: colors.actionOnLight,
			actionBorder: transparentize(0.88, colors.black),
			actionHover: darken(0.1, colors.actionOnLight),
			actionDisabled: '#83B8ED',
			actionDisabledLabel: colors.disabledText,
			actionLight: '#DCEDFF',
			link: colors.actionOnLight,
			backgroundSplash: '#F1F2F4',
			pageHeader: '#00005E',

			numberPositive: colors.amountPositive,
			numberNegative: colors.amountNegative,

			iconBackground: '#E3E3E3',
			iconBackgroundHighlighted: '#00005E',

			headerIconPrimary: '#0000A0',
			headerIconPrimaryAccent: '#83B8ED',
			headerIconBackground: colors.white,
			headerIconGradientStart: colors.white,
			headerIconGradientStop: transparentize(0.8, colors.hero),

			warning: colors.warning,

			text: colors.text,
			textMuted: '#5A575C',
			textContrast: colors.white,
			border: colors.navBorders,
			background: colors.background,
			separator: '#eae9e9',
			scrollbarThumb: '#ADB1B2',

			disabled: '#808080',
			disabledContrast: '#E0E0E0',

			inputFocus: '#0000A0',
			inputBorder: '#C9C7C7',
			inputDisabled: '#F1F2F4',
			inputIcon: '#00005E',
			inputIconBackground: '#F1F2F4',

			input: {
				...defaultInputTheme,
				label: colors.text,

				focusOutline: colors.actionOnLight,

				background: colors.white,
				backgroundHover: colors.white,
				backgroundReadOnly: colors.white,
				backgroundError: transparentize(0.91, colors.alert),
				backgroundErrorHover: transparentize(0.91, colors.alert),

				border: '#C9C7C7',
				borderHover: colors.actionOnLight,
				borderError: colors.alert,
				borderOutlineHover: colors.actionOnLight,
				borderOutlineError: colors.alert,

				menuIndicator: transparentize(0.88, colors.black),
				menuIndicatorActive: colors.black,
				menuOptionCheckMark: colors.actionOnLight,

				menuOptionBackgroundHighlighted: transparentize(0.8, colors.actionOnLight),
				menuOptionBackgroundSelected: transparentize(0.9, colors.actionOnLight),
				menuOptionBackgroundHover: transparentize(0.8, colors.actionOnLight),
			},

			// Required because of the theme set in @subaio/components
			primary: colors.actionOnLight,
			danger: colors.alert,
			textHeader: colors.text,

			// Required because of the theme set in @subaio/webapp
			/** System status indicators */
			status: {
				operational: '#5EB870',
				minor: '#FCB900',
				major: '#F27641',
				critical: '#E15554',
			},

			button: defaultButtonTheme,
		},
		animation,
		font,
		spacing: defaultSpacing,
		borderRadius: '4px',
		colorScheme: 'light',
	}
}

export const getTheme = () => ({
	...theme(),
})

export const helpers = themeHelpers(getTheme())

export type Theme = ReturnType<typeof getTheme>
